import AsyncStorage from '@react-native-async-storage/async-storage';

const KEYS = {
  ID_TOKEN: '@eleplan/id_token',
  ACCESS_TOKEN: '@eleplan/access_token',
  REFRESH_TOKEN: '@eleplan/refresh_token',
  REFRESH_CLIENT_ID: '@eleplan/refresh_client_id',
  USERNAME: '@eleplan/username',
  EXPIRES_AT: '@eleplan/expires_at',
};

export interface StoredTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  refreshClientId: string;
  username: string;
  expiresAt: number;
}

export async function storeTokens(tokens: {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  refreshClientId?: string;
  username: string;
  expiresIn: number;
}): Promise<void> {
  const expiresAt = Date.now() + tokens.expiresIn * 1000;
  await AsyncStorage.multiSet([
    [KEYS.ID_TOKEN, tokens.idToken],
    [KEYS.ACCESS_TOKEN, tokens.accessToken],
    [KEYS.REFRESH_TOKEN, tokens.refreshToken],
    [KEYS.REFRESH_CLIENT_ID, tokens.refreshClientId || ''],
    [KEYS.USERNAME, tokens.username],
    [KEYS.EXPIRES_AT, String(expiresAt)],
  ]);
}

export async function getStoredTokens(): Promise<StoredTokens | null> {
  const pairs = await AsyncStorage.multiGet([
    KEYS.ID_TOKEN,
    KEYS.ACCESS_TOKEN,
    KEYS.REFRESH_TOKEN,
    KEYS.REFRESH_CLIENT_ID,
    KEYS.USERNAME,
    KEYS.EXPIRES_AT,
  ]);

  const map = Object.fromEntries(pairs.map(([k, v]) => [k, v]));
  const idToken = map[KEYS.ID_TOKEN];
  const accessToken = map[KEYS.ACCESS_TOKEN];
  const refreshToken = map[KEYS.REFRESH_TOKEN];
  const refreshClientId = map[KEYS.REFRESH_CLIENT_ID];
  const username = map[KEYS.USERNAME];
  const expiresAt = Number(map[KEYS.EXPIRES_AT] || 0);

  if (!idToken || !accessToken || !refreshToken) return null;

  return {
    idToken,
    accessToken,
    refreshToken,
    refreshClientId: refreshClientId || '',
    username: username || '',
    expiresAt
  };
}

export async function getIdToken(): Promise<string> {
  const token = await AsyncStorage.getItem(KEYS.ID_TOKEN);
  return token || '';
}

export async function getAccessToken(): Promise<string> {
  const token = await AsyncStorage.getItem(KEYS.ACCESS_TOKEN);
  return token || '';
}

export async function isTokenExpired(): Promise<boolean> {
  const expiresAt = await AsyncStorage.getItem(KEYS.EXPIRES_AT);
  if (!expiresAt) return true;
  return Date.now() > Number(expiresAt) - 60_000;
}

export async function clearTokens(): Promise<void> {
  await AsyncStorage.multiRemove(Object.values(KEYS));
}
